import { PII_BLOCK_CLASS } from "@/lib/analytics";
import { stripePromise } from "@/lib/payment";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe
} from "@stripe/react-stripe-js";
import type {
  StripeError,
  StripePaymentElementOptions
} from "@stripe/stripe-js";
import { Lock } from "lucide-react";
import {
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useState
} from "react";

// ---- Public ref interface ----

/** Why a Stripe step failed: the message, plus Stripe's own classification. */
export interface StripeFailure {
  error?: string;
  /** Stripe's error `type`; absent for a failure that did not come from Stripe. */
  errorType?: StripeError["type"];
  /** Stripe's error `code`, when it sent one. */
  errorCode?: StripeError["code"];
}

export interface StripePaymentFormRef {
  /**
   * Validates the form and creates N fresh PaymentMethods from the card data.
   * Must be called directly from the click handler — Stripe requires
   * elements.submit() to run before any other async work.
   *
   * A separate PM is created per cart because Stripe Connect doesn't allow
   * PM reuse across connected accounts without Customer attachment.
   */
  submitAndCreatePMs: (count: number) => Promise<
    StripeFailure & {
      success: boolean;
      paymentMethodIds?: string[];
    }
  >;
  /**
   * Confirms a PaymentIntent using a pre-created PaymentMethod ID.
   * Call submitAndCreatePMs first to get the PM IDs.
   */
  confirmCardPayment: (
    clientSecret: string,
    paymentMethodId: string
  ) => Promise<StripeFailure & { success: boolean }>;
}

// ---- Inner form (has access to Stripe hooks) ----

interface InnerFormProps {
  cartId: string;
  onChange?: () => void;
}

const InnerPaymentForm = forwardRef<StripePaymentFormRef, InnerFormProps>(
  function InnerPaymentForm({ cartId, onChange }, ref) {
    const stripe = useStripe();
    const elements = useElements();
    const [isReady, setIsReady] = useState(false);

    const handleReady = useCallback(() => {
      setIsReady(true);
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        submitAndCreatePMs: async (count: number) => {
          if (!stripe || !elements) {
            return { success: false, error: "Stripe not loaded" };
          }

          // 1. Submit / validate the PaymentElement form.
          //    Must happen before createPaymentMethod and before any other
          //    async work (Stripe requirement).
          const { error: submitError } = await elements.submit();
          if (submitError) {
            return {
              success: false,
              error: submitError.message || "Please check your payment details",
              errorType: submitError.type,
              errorCode: submitError.code
            };
          }

          // 2. Create one fresh PM per cart, back-to-back.
          const paymentMethodIds: string[] = [];
          for (let i = 0; i < count; i++) {
            const { error: pmError, paymentMethod } =
              await stripe.createPaymentMethod({ elements });
            if (pmError || !paymentMethod) {
              return {
                success: false,
                error: pmError?.message || "Failed to create payment method",
                errorType: pmError?.type,
                errorCode: pmError?.code
              };
            }
            paymentMethodIds.push(paymentMethod.id);
          }

          return { success: true, paymentMethodIds };
        },

        confirmCardPayment: async (
          clientSecret: string,
          paymentMethodId: string
        ) => {
          if (!stripe) {
            return { success: false, error: "Stripe not loaded" };
          }

          const { error } = await stripe.confirmCardPayment(clientSecret, {
            payment_method: paymentMethodId
          });

          if (error) {
            return {
              success: false,
              error: error.message || "Payment confirmation failed",
              errorType: error.type,
              errorCode: error.code
            };
          }

          return { success: true };
        }
      }),
      [stripe, elements]
    );

    const paymentElementOptions: StripePaymentElementOptions = {
      layout: "tabs"
    };

    return (
      // PII_BLOCK_CLASS, not PII_MASK_CLASS: this is the card-entry surface, so
      // it is blocked from replays outright rather than text-masked (KAN-44 §9,
      // ADR-0006). rrweb cannot see into Stripe's cross-origin iframes anyway -
      // this is the belt to that braces, and it is scoped to the payment element
      // rather than the checkout page, which the pilot still needs to watch.
      <div className={PII_BLOCK_CLASS}>
        <PaymentElement
          id={`payment-element-${cartId}`}
          options={paymentElementOptions}
          onReady={handleReady}
          onChange={onChange}
        />
        {!isReady && (
          <div className="flex items-center justify-center py-6">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-purple-600 border-t-transparent" />
          </div>
        )}
      </div>
    );
  }
);

// ---- Outer wrapper (provides <Elements> context) ----

interface StripePaymentFormProps {
  cartId: string;
  /** Total amount in smallest currency unit (cents) — used for PaymentElement display */
  amount: number;
  /** ISO currency code (e.g. "aud") */
  currency: string;
  onChange?: () => void;
}

export const StripePaymentForm = memo(
  forwardRef<StripePaymentFormRef, StripePaymentFormProps>(
    function StripePaymentForm({ cartId, amount, currency, onChange }, ref) {
      return (
        <div className="space-y-3">
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <Lock className="h-3 w-3" />
            <span>All transactions are secure and encrypted</span>
          </div>

          <Elements
            stripe={stripePromise}
            options={{
              mode: "payment",
              amount,
              currency,
              paymentMethodCreation: "manual",
              appearance: {
                theme: "stripe",
                variables: {
                  colorPrimary: "#9333ea",
                  borderRadius: "8px",
                  fontFamily: "system-ui, -apple-system, sans-serif"
                }
              }
            }}
          >
            <InnerPaymentForm ref={ref} cartId={cartId} onChange={onChange} />
          </Elements>
        </div>
      );
    }
  )
);
